import type {IssueField} from '@github-ui/item-picker/IssueFieldPicker'
import {graphql} from 'relay-runtime'
import type {FieldsSectionFragment$key} from './__generated__/FieldsSectionFragment.graphql'
import {useFragment} from 'react-relay'
import type React from 'react'
import {useCallback, useState, useEffect} from 'react'
import type {FieldsSectionFieldValues$key} from './__generated__/FieldsSectionFieldValues.graphql'
import {IssueFieldSingleSelectEditor, IssueFieldTextEditor} from './FieldEditors'
import type {IssueFieldSingleSelectOption} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {
  isIssueFieldWithUnsavedValue,
  isSingleSelectField,
  isSingleSelectFieldWithValue,
  isTextField,
  type IssueFieldWithUnsavedValue,
} from '../../utils/issue-field-helper'
import {FieldsSelector} from './FieldSelector'

export type CreateIssueFieldsSectionProps = {
  owner: string
  onSelectionChange: (fields: IssueFieldWithUnsavedValue[]) => void
  shortcutEnabled: boolean
}

export function CreateIssueFieldsSection({owner, onSelectionChange, shortcutEnabled}: CreateIssueFieldsSectionProps) {
  const isReadonly = false
  const [fields, setFields] = useState<IssueFieldWithUnsavedValue[]>([])
  const [fieldsSet, setFieldsSet] = useState<string[]>([])

  useEffect(() => {
    onSelectionChange(fields)
  }, [fields, onSelectionChange])

  const onIssueFieldSelected = useCallback(
    (selectedField: IssueField, selectedOption?: IssueFieldSingleSelectOption | null) => {
      if (isTextField(selectedField)) {
        setFields(prevFields => {
          const newFields = [...prevFields]
          if (selectedField.name && !newFields.some(f => f.field.name === selectedField.name)) {
            newFields.push({field: selectedField, value: ''})
          }
          return newFields
        })
      } else if (isSingleSelectField(selectedField) && selectedOption !== undefined) {
        setFields(prevFields => {
          const newFields = [...prevFields]
          if (selectedField.name && !newFields.some(f => f.field.name === selectedField.name)) {
            newFields.push({
              field: selectedField,
              value: selectedOption,
            })
          }
          return newFields
        })
      }
    },
    [],
  )

  const onCommitTextField = useCallback((fieldId: string, value: string) => {
    setFields(prevFields => {
      return prevFields.map(field => {
        if (isIssueFieldWithUnsavedValue(field) && field.field.id === fieldId) {
          return {...field, value}
        }
        return field
      })
    })
  }, [])

  const onCommitSingleSelectField = useCallback((fieldId: string, value: IssueFieldSingleSelectOption | null) => {
    setFields(prevFields => {
      return prevFields.map(field => {
        if (isSingleSelectFieldWithValue(field) && field.field.id === fieldId) {
          return {...field, value}
        }
        return field
      })
    })
  }, [])

  return (
    <>
      {fields.map((field, index) => {
        if (!field.field.id) return null
        const isLastItem = index === fields.length - 1
        if (isIssueFieldWithUnsavedValue(field)) {
          return (
            <IssueFieldTextEditor
              key={field.field.name}
              fieldId={field.field.id}
              fieldName={field.field.name || ''}
              initialValue={field.value || ''}
              onCommit={onCommitTextField}
              hideDivider={isLastItem}
            />
          )
        } else if (isSingleSelectFieldWithValue(field)) {
          return (
            <IssueFieldSingleSelectEditor
              key={field.field.name}
              fieldId={field.field.id}
              fieldName={field.field.name || ''}
              initialValue={
                field.value
                  ? {
                      name: field.value.name || '',
                      color: field.value.color || '',
                      description: field.value.description || '',
                    }
                  : null
              }
              onCommit={onCommitSingleSelectField}
              hideDivider={isLastItem}
            />
          )
        }
      })}
      <FieldsSelector
        owner={owner}
        fieldsSet={fieldsSet}
        setFieldsSet={setFieldsSet}
        onIssueFieldSelected={onIssueFieldSelected}
        isReadonly={isReadonly}
        shortcutEnabled={shortcutEnabled}
      />
    </>
  )
}

export type EditIssueFieldsSectionProps = {
  onIssueUpdate?: () => void
  singleKeyShortcutsEnabled: boolean
  issue: FieldsSectionFragment$key
  insideSidePanel?: boolean
}

export function EditIssueFieldsSection({issue, singleKeyShortcutsEnabled}: EditIssueFieldsSectionProps) {
  // TODO issue_fields
  const isReadonly = false
  const [fieldsSet, setFieldsSet] = useState<string[]>([])

  const data = useFragment(
    graphql`
      fragment FieldsSectionFragment on Issue {
        id
        repository {
          owner {
            login
          }
        }
        ...FieldsSectionFieldValues
      }
    `,
    issue,
  )
  const {
    repository: {
      owner: {login: owner},
    },
  } = data

  const onIssueFieldSelected = useCallback(
    // eslint-disable-next-line unused-imports/no-unused-vars
    (selectedField: IssueField, selectedOption?: IssueFieldSingleSelectOption | null) => {
      // TODO issue_field call set field value mutation
    },
    [],
  )

  return (
    <>
      <IssueFields issue={data} fieldsSet={fieldsSet} setFieldsSet={setFieldsSet} />
      <FieldsSelector
        owner={owner}
        fieldsSet={fieldsSet}
        setFieldsSet={setFieldsSet}
        onIssueFieldSelected={onIssueFieldSelected}
        isReadonly={isReadonly}
        shortcutEnabled={singleKeyShortcutsEnabled}
      />
    </>
  )
}

function IssueFields({
  issue,
  fieldsSet,
  setFieldsSet,
}: {
  issue: FieldsSectionFieldValues$key
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
}) {
  const data = useFragment(
    graphql`
      fragment FieldsSectionFieldValues on Issue {
        id
        issueFieldValues(first: 25) {
          nodes {
            ... on IssueFieldTextValue {
              field {
                ... on IssueFieldText {
                  id
                  name
                  dataType
                }
              }
              value
            }
            ... on IssueFieldSingleSelectValue {
              field {
                ... on IssueFieldSingleSelect {
                  id
                  name
                  dataType
                }
              }
              name
              color
              description
            }
          }
        }
      }
    `,
    issue,
  )

  const filteredFieldValues = (data.issueFieldValues?.nodes || []).filter(fv => !!fv && fv.field)

  const filteredFieldsNames = filteredFieldValues.map(fv => fv?.field?.name || '')

  useEffect(() => {
    if (fieldsSet.join(',') !== filteredFieldsNames.join(',') && filteredFieldsNames.length > 0) {
      setFieldsSet(filteredFieldsNames)
    }
  }, [fieldsSet, filteredFieldsNames, setFieldsSet])

  // eslint-disable-next-line unused-imports/no-unused-vars
  const onCommitTextField = useCallback((fieldId: string, value: string) => {
    // TODO issue_field call set field value mutation
  }, [])

  // eslint-disable-next-line unused-imports/no-unused-vars
  const onCommitSingleSelectField = useCallback((fieldId: string, value: IssueFieldSingleSelectOption | null) => {
    // TODO issue_field call set field value mutation
  }, [])

  const fieldValues = filteredFieldValues.map((fieldValue, index) => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    const isLastItem = index === filteredFieldValues.length - 1

    if (fieldValue.field && fieldValue.field.dataType === 'TEXT') {
      return (
        <IssueFieldTextEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={fieldValue.value || ''}
          onCommit={onCommitTextField}
          hideDivider={isLastItem}
        />
      )
    } else if (fieldValue.field.dataType === 'SINGLE_SELECT') {
      return (
        <IssueFieldSingleSelectEditor
          key={fieldValue.field.name}
          fieldId={fieldValue.field.id}
          fieldName={fieldValue.field.name || ''}
          initialValue={{
            name: fieldValue.name || '',
            color: fieldValue.color || '',
            description: fieldValue.description || '',
          }}
          onCommit={onCommitSingleSelectField}
          hideDivider={isLastItem}
        />
      )
    } else {
      return null
    }
  })

  return <>{fieldValues}</>
}

try{ CreateIssueFieldsSection.displayName ||= 'CreateIssueFieldsSection' } catch {}
try{ EditIssueFieldsSection.displayName ||= 'EditIssueFieldsSection' } catch {}
try{ IssueFields.displayName ||= 'IssueFields' } catch {}